import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { serializeError } from "@-/com.npmjs.serialize-error";
import {
  BottomSheet,
  type BottomSheetHandle,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { copyToClipboard } from "@-/dev.improve.haniverse.lib.clipboard";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { errorDetailsRef } from "#show-error-details";

/** Mounted in the app shell so dismissing a toast never dismisses its details. */
export function ErrorDetailsSheet() {
  const sheetRef = useRef<BottomSheetHandle>(null);
  const [request, setRequest] = useState<{ details: string } | null>(null);
  useImperativeHandle(
    errorDetailsRef,
    () => ({
      show(error) {
        setRequest({ details: JSON.stringify(serializeError(error), null, 2) ?? String(error) });
      },
    }),
    [],
  );
  useEffect(() => {
    if (request !== null) {
      sheetRef.current?.open();
    }
  }, [request]);

  return (
    <BottomSheet
      footer={
        request === null ? undefined : (
          <CopyErrorDetailsButton details={request.details} key={request.details} />
        )
      }
      onDismiss={() => setRequest(null)}
      ref={sheetRef}
      scrollable
    >
      <View className="gap-4 pb-4">
        <View className="flex-row items-center justify-between">
          <Text accessibilityRole="header" className="text-xl font-semibold">
            Error Details
          </Text>
          <Pressable
            accessibilityRole="button"
            className="px-3 py-2"
            onPress={() => sheetRef.current?.close()}
          >
            <Text className="text-primary">Close</Text>
          </Pressable>
        </View>
        <View className="rounded-xs border border-border bg-muted p-3">
          <Text className="font-mono text-xs leading-5" selectable>
            {request?.details}
          </Text>
        </View>
      </View>
    </BottomSheet>
  );
}

function CopyErrorDetailsButton({ details }: { details: string }) {
  const [status, setStatus] = useState<"ready" | "copying" | "copied" | "failed">("ready");
  const label = status === "copied" ? "Copied!" : "Copy Error Details";
  const copy = async () => {
    setStatus("copying");
    try {
      await copyToClipboard(details, { showSuccessToast: false });
      setStatus("copied");
    } catch (error) {
      logger(import.meta.location).warn("Could not copy error details", error);
      setStatus("failed");
    }
  };

  return (
    <View className="gap-2">
      {status === "failed" ? (
        <Text accessibilityRole="alert" className="text-sm text-destructive">
          Couldn&apos;t copy error details. Please try again.
        </Text>
      ) : null}
      <Pressable
        accessibilityLabel={label}
        accessibilityRole="button"
        className="items-center rounded-xs bg-primary px-4 py-3"
        disabled={status === "copying" || status === "copied"}
        onPress={() => {
          void copy();
        }}
      >
        <Text accessible={false} aria-hidden className="text-sm leading-5 font-semibold opacity-0">
          Copy Error Details
        </Text>
        <View className="absolute inset-0 items-center justify-center" pointerEvents="none">
          <Text className="text-sm leading-5 font-semibold text-primary-foreground">{label}</Text>
        </View>
      </Pressable>
    </View>
  );
}
